import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { insertionBoundary, uniqueRowIndex } from './paperOrder';

export const PAPER_ROW_DND_MIME = 'application/x-ky-paper-row';
export const PROBLEM_DND_MIME = 'application/x-ky-problem';

// 짧은 목록에서도 양쪽 가장자리 영역이 겹치지 않게 높이의 1/4로 제한한다.
export const DRAG_EDGE_PX = 48;
export const DRAG_MAX_SPEED = 720; // 초당 CSS px. 가장자리에 가까울수록 빨라진다.
const MAX_FRAME_MS = 50; // 탭 복귀 시 누적 시간만큼 갑자기 이동하지 않는다.

export function dragScrollSpeed(rect, x, y) {
  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return 0;
  const edge = Math.min(DRAG_EDGE_PX, rect.height / 4);
  if (edge <= 0) return 0;
  if (y < rect.top + edge) return -DRAG_MAX_SPEED * (1 - (y - rect.top) / edge);
  if (y > rect.bottom - edge) return DRAG_MAX_SPEED * (1 - (rect.bottom - y) / edge);
  return 0;
}

/** 이 편집기에서 시작한 드래그만 받는다. 위치 표시는 변경하되 문항 배열은 drop에서만 변경한다. */
export default function usePaperListDrag({ rows, enabled, onMove, onInsert }) {
  const listRef = useRef(null);
  const session = useRef(null);
  const frame = useRef(null);
  const point = useRef(null);
  const lastTime = useRef(null);
  const [source, setSource] = useState(null);
  const [dropAt, setDropAt] = useState(null);
  const live = useRef({ rows, enabled, onMove, onInsert });
  useLayoutEffect(() => { live.current = { rows, enabled, onMove, onInsert }; });

  const leave = useCallback(() => {
    if (frame.current != null) cancelAnimationFrame(frame.current);
    frame.current = null;
    point.current = null;
    lastTime.current = null;
    setDropAt(null);
  }, []);
  const end = useCallback(() => {
    leave();
    session.current = null;
    setSource(null);
  }, [leave]);

  // 저장·교체 모드 진입, 목록 재설정, 닫기에서도 오래된 드래그를 사용할 수 없다.
  useLayoutEffect(() => {
    end();
    return end;
  }, [rows, enabled, end]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') end(); };
    document.addEventListener('dragend', end);
    document.addEventListener('drop', end); // React의 drop 처리 뒤에 정리한다.
    document.addEventListener('keydown', onKey);
    window.addEventListener('blur', end);
    return () => {
      document.removeEventListener('dragend', end);
      document.removeEventListener('drop', end);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', end);
    };
  }, [enabled, end]);

  const boundaryAt = (y) => insertionBoundary(
    Array.from(listRef.current?.querySelectorAll('[data-paper-row]') || []).map((el) => el.getBoundingClientRect()), y,
  );
  const inside = (x, y) => {
    const rect = listRef.current?.getBoundingClientRect();
    return rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  };
  const tick = (time) => {
    frame.current = null;
    const box = listRef.current;
    const pos = point.current;
    if (!box || !pos || !session.current || !live.current.enabled) return;
    const elapsed = lastTime.current == null ? 0 : Math.min(time - lastTime.current, MAX_FRAME_MS);
    lastTime.current = time;
    const speed = dragScrollSpeed(box.getBoundingClientRect(), pos.x, pos.y);
    box.scrollTop = Math.max(0, Math.min(box.scrollHeight - box.clientHeight, box.scrollTop + speed * elapsed / 1000));
    setDropAt(boundaryAt(pos.y));
    frame.current = requestAnimationFrame(tick);
  };
  const accepts = (event) => {
    const drag = session.current;
    if (!drag || !live.current.enabled || drag.rows !== live.current.rows) return false;
    const mime = drag.kind === 'move' ? PAPER_ROW_DND_MIME : PROBLEM_DND_MIME;
    return Array.from(event.dataTransfer?.types || []).includes(mime);
  };
  const start = (event, id, kind) => {
    if (!live.current.enabled || (kind === 'move' && uniqueRowIndex(live.current.rows, id) < 0)) {
      event.preventDefault();
      return;
    }
    end();
    const drag = { id, kind, rows: live.current.rows };
    const el = event.currentTarget.closest('li');
    if (el && event.dataTransfer.setDragImage) {
      const rect = el.getBoundingClientRect();
      event.dataTransfer.setDragImage(el, event.clientX - rect.left, event.clientY - rect.top);
    }
    event.dataTransfer.setData(kind === 'move' ? PAPER_ROW_DND_MIME : PROBLEM_DND_MIME, JSON.stringify({ id }));
    event.dataTransfer.setData('text/plain', `#${id}`);
    event.dataTransfer.effectAllowed = kind;
    session.current = drag;
    setSource({ id, kind });
  };
  const onDragOverCapture = (event) => {
    if (!accepts(event) || !inside(event.clientX, event.clientY)) { leave(); return; }
    event.preventDefault();
    event.dataTransfer.dropEffect = session.current.kind;
    point.current = { x: event.clientX, y: event.clientY };
    setDropAt(boundaryAt(event.clientY));
    if (frame.current == null) frame.current = requestAnimationFrame(tick);
  };
  const onDrop = (event) => {
    if (!accepts(event) || !inside(event.clientX, event.clientY)) { end(); return; }
    event.preventDefault();
    event.stopPropagation();
    const drag = session.current;
    const boundary = boundaryAt(event.clientY);
    end();
    if (drag.kind === 'move') live.current.onMove(drag.id, boundary);
    else live.current.onInsert(drag.id, boundary);
  };
  const onDragLeave = (event) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
    // WebKit은 자식 사이 이동에서도 relatedTarget을 비울 수 있다. 실제 목록 이탈만 멈춘다.
    if (event.relatedTarget == null && inside(event.clientX, event.clientY)) return;
    leave();
  };
  return {
    listRef, source, dropAt, start, end,
    listProps: { onDragEnterCapture: onDragOverCapture, onDragOverCapture, onDrop, onDragLeave },
  };
}
